"use client";

import { METRICS_EVENTS } from "@cede/types";
import { formatBalance } from "@cede/utils";
import { UsdtIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/ui/info-card";
import { SkeletonElement } from "@/components/ui/skeleton";
import { useCashbackStatus, useCashbackConfig, useDevice } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useCashbackDialogStore } from "@/store/cashbackDialogStore";
import Decimal from "decimal.js";

export const CashbackCard = () => {
  const { isMobile } = useDevice();
  const { status: cashbackStatus, isLoading: isLoadingCashback } = useCashbackStatus();
  const { isActive: isCashbackActive } = useCashbackConfig();
  const { handleOpen: handleOpenCashback } = useCashbackDialogStore();
  const { trackEvent } = useMetricsEvents();

  const handleOpenCashbackClick = () => {
    trackEvent(METRICS_EVENTS.CASHBACK_BUTTON_CLICKED, {});
    handleOpenCashback();
  };

  const cashbackAccumulated = cashbackStatus?.accumulated
    ? new Decimal(cashbackStatus.accumulated.amount)
    : new Decimal(0);
  const cashbackClaimable = cashbackStatus?.claimable ? new Decimal(cashbackStatus.claimable.amount) : new Decimal(0);
  const cashbackPending = cashbackStatus?.unlockProgress?.current
    ? new Decimal(cashbackStatus.unlockProgress.current)
    : new Decimal(0);
  const cashbackProgress = cashbackStatus?.unlockProgress?.percentage ?? 0;

  return (
    <InfoCard
      title="Cashback"
      padding={isMobile ? "none" : "md"}
      withBackground={!isMobile}
      withBorder={!isMobile}
      gap="lg"
      className="flex min-w-0 flex-col"
      contentClassName="flex flex-1 flex-col justify-between gap-5"
      titleClassName="text-lg font-semibold text-cede-text-primary"
    >
      {isCashbackActive ? (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              {isLoadingCashback ? (
                <SkeletonElement className="h-[60px] w-[100px] rounded-lg" />
              ) : (
                <span className="text-5xl font-bold text-cede-text-accent flex items-center gap-2">
                  <UsdtIcon width={40} height={40} className="shrink-0" />
                  {formatBalance(
                    cashbackClaimable.greaterThan(0)
                      ? cashbackClaimable
                      : cashbackPending.greaterThan(0)
                        ? cashbackPending
                        : cashbackAccumulated,
                    {
                      roundForFiat: true,
                    },
                  )}
                </span>
              )}
            </div>

            {!isLoadingCashback && cashbackPending.greaterThan(0) && cashbackClaimable.equals(0) && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-cede-text-secondary">Progress to unlock</span>
                  <span className="font-semibold text-cede-text-primary">{cashbackProgress.toFixed(0)}%</span>
                </div>
                <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-contrast-shade-primary-30">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-cede-primary transition-all duration-300 ease-out"
                    style={{ width: `${cashbackProgress}%` }}
                  />
                </div>
              </div>
            )}

            {!isLoadingCashback && cashbackClaimable.greaterThan(0) && (
              <div className="flex items-start py-1">
                <span className="text-sm font-medium text-cede-primary">Ready to claim!</span>
              </div>
            )}

            {!isLoadingCashback &&
              cashbackPending.equals(0) &&
              cashbackAccumulated.equals(0) &&
              cashbackClaimable.equals(0) && (
                <div className="flex items-start py-2">
                  <span className="text-sm italic text-cede-text-secondary">Trade to earn cashback!</span>
                </div>
              )}
          </div>
          <Button variant="secondary" size="medium" onClick={handleOpenCashbackClick} className="w-full">
            View Cashback
          </Button>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            <div className="flex items-start py-2">
              <span className="text-sm italic text-cede-text-secondary">Coming soon</span>
            </div>
          </div>
          <Button variant="secondary" size="medium" onClick={handleOpenCashbackClick} className="w-full">
            View Cashback
          </Button>
        </>
      )}
    </InfoCard>
  );
};
